import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError, resolveImage } from "../../lib/api";
import { Shell } from "./_shell";
import { ImagePicker, ExcelToolbar, InventoryToolbar } from "./_shared";

const EMPTY_PRODUCT = {
  name: "", category_slug: "", brand: "", description: "", image_url: "",
  sku: "", barcode: "", unit: "case", unit_size: "", case_size: "",
  is_imported: false, is_featured: false, is_coming_soon: false, is_visible: true,
  availability: "in_stock", display_order: 0,
  wholesale_price: "", min_order_qty: "", low_stock_threshold: "", ordering_enabled: false,
  stock_quantity: "", track_inventory: false,
  expiry_date: "", batch_number: "", payment_method: "electronic_or_cheque",
};

export default function Products() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = () => api.get("/products").then(r => setList(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/categories").then(r => { setCats(r.data); }).catch(() => {});
    api.get("/brands").then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const brandsForCat = (slug) => brands.filter(b => (b.category_slugs || []).includes(slug));

  const openNew = () => {
    setEditId(null);
    setForm({ ...EMPTY_PRODUCT, category_slug: cats[0]?.slug || "" });
    setShowForm(true); setShowAdvanced(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.name || !form.category_slug) { toast.error("Name and category are required"); return; }
    try {
      const num = (v) => (v === "" || v == null ? null : parseFloat(v));
      const int = (v) => (v === "" || v == null ? null : parseInt(v));
      const payload = { ...form,
        wholesale_price: num(form.wholesale_price),
        min_order_qty: int(form.min_order_qty), display_order: parseInt(form.display_order) || 0,
        stock_quantity: int(form.stock_quantity),
      };
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Product updated" : "Product created");
      setForm({ ...EMPTY_PRODUCT, category_slug: cats[0]?.slug || "" });
      setEditId(null); setShowForm(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const editProduct = (p) => {
    setEditId(p.id);
    setForm({ ...EMPTY_PRODUCT, ...p,
      wholesale_price: p.wholesale_price ?? "", min_order_qty: p.min_order_qty ?? "",
      stock_quantity: p.stock_quantity ?? "" });
    setShowForm(true); setShowAdvanced(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const duplicate = (p) => {
    setEditId(null);
    setForm({ ...EMPTY_PRODUCT, ...p, name: `${p.name} (copy)`, sku: "",
      wholesale_price: p.wholesale_price ?? "", min_order_qty: p.min_order_qty ?? "" });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Editing a copy — Save to create it");
  };

  const toggleVisible = async (p) => {
    try {
      await api.put(`/products/${p.id}`, { ...p, is_visible: !p.is_visible,
        wholesale_price: p.wholesale_price ?? null, min_order_qty: p.min_order_qty ?? null, low_stock_threshold: p.low_stock_threshold ?? null });
      toast.success(p.is_visible ? "Hidden" : "Published"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const catBySlug = Object.fromEntries(cats.map(c => [c.slug, c]));
  const filtered = list.filter(p => (filter === "all" || p.category_slug === filter)
    && (!search || `${p.name} ${p.brand} ${p.sku}`.toLowerCase().includes(search.toLowerCase())));
  const availableBrands = brandsForCat(form.category_slug);

  return (
    <Shell active="products">
      <div className="p-5 md:p-10">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <div className="overline mb-2">Catalogue · {list.length} products</div>
            <h1 className="font-display text-4xl md:text-6xl uppercase leading-none">Products.</h1>
          </div>
          <button onClick={showForm ? () => setShowForm(false) : openNew} className="btn-primary" data-testid="btn-add-product">
            {showForm ? "Close form" : "Add product"}
          </button>
        </div>

        <ExcelToolbar onImported={() => { load(); }} />
        <InventoryToolbar onImported={() => { load(); }} />

        {showForm && (
          <div className="border border-ink hard-shadow p-5 md:p-6 mb-8 bg-paper" data-testid="product-form">
            <div className="overline mb-4">{editId ? "Edit product" : "New product"}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="overline block mb-2">Product name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="field" data-testid="pf-name" />
              </div>
              <div>
                <label className="overline block mb-2">Category *</label>
                <select value={form.category_slug} onChange={e => setForm({ ...form, category_slug: e.target.value, brand: "" })} className="field" data-testid="pf-category">
                  <option value="">Select category…</option>
                  {cats.map(c => <option key={c.id} value={c.slug}>{c.name}{c.section === "imported" ? " · Imported" : ""}</option>)}
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Brand</label>
                <select value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} className="field" data-testid="pf-brand">
                  <option value="">— No brand —</option>
                  {availableBrands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                </select>
                {form.category_slug && availableBrands.length === 0 && (
                  <p className="text-[10px] text-soda mt-1">No brands assigned to this category yet. Add one under Brands.</p>
                )}
              </div>
              <div>
                <label className="overline block mb-2">SKU</label>
                <input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} placeholder="e.g. COKE-355-24" className="field" data-testid="pf-sku" />
              </div>
              <div>
                <label className="overline block mb-2">Barcode (optional)</label>
                <input value={form.barcode} onChange={e => setForm({ ...form, barcode: e.target.value })} placeholder="UPC / EAN" className="field" data-testid="pf-barcode" />
              </div>
              <div>
                <label className="overline block mb-2">Unit size</label>
                <input value={form.unit_size} onChange={e => setForm({ ...form, unit_size: e.target.value })} placeholder="e.g. 355ml" className="field" data-testid="pf-size" />
              </div>
              <div>
                <label className="overline block mb-2">Case size</label>
                <input value={form.case_size} onChange={e => setForm({ ...form, case_size: e.target.value })} placeholder="e.g. 24 cans" className="field" data-testid="pf-casesize" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Description</label>
                <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="field" data-testid="pf-desc" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Product image</label>
                <ImagePicker value={form.image_url} onChange={v => setForm({ ...form, image_url: v })} testid="pf-image" label="product photo" />
              </div>
              <div>
                <label className="overline block mb-2">Availability</label>
                <select value={form.availability} onChange={e => setForm({ ...form, availability: e.target.value })} className="field" data-testid="pf-availability">
                  <option value="in_stock">In stock</option>
                  <option value="limited">Limited</option>
                  <option value="out_of_stock">Out of stock</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Stock quantity</label>
                <input type="number" min="0" value={form.stock_quantity} onChange={e => setForm({ ...form, stock_quantity: e.target.value, track_inventory: e.target.value !== "" })} placeholder="—" className="field" data-testid="pf-stock" />
                <div className="text-[10px] text-black/50 mt-1">Leave blank to skip inventory tracking. Bulk-edit via Inventory Excel above.</div>
              </div>
              <div>
                <label className="overline block mb-2">Expiry date (optional)</label>
                <input type="date" value={form.expiry_date || ""} onChange={e => setForm({ ...form, expiry_date: e.target.value })} className="field" data-testid="pf-expiry" />
                <div className="text-[10px] text-black/50 mt-1">Products expiring in ≤ 30 days show up in the Expiry Alert page.</div>
              </div>
              <div>
                <label className="overline block mb-2">Batch number (optional)</label>
                <input value={form.batch_number || ""} onChange={e => setForm({ ...form, batch_number: e.target.value })} placeholder="e.g. LOT-2026-A" className="field" data-testid="pf-batch" />
              </div>
              <div>
                <label className="overline block mb-2">Payment method (required)</label>
                <select value={form.payment_method || "electronic_or_cheque"} onChange={e => setForm({ ...form, payment_method: e.target.value })} className="field" data-testid="pf-payment">
                  <option value="cash">Cash</option>
                  <option value="electronic_or_cheque">E-Transfer / Cheque</option>
                </select>
                <div className="text-[10px] text-black/50 mt-1">Determines how retailers must pay when this product is in their order. Customers can't override this.</div>
              </div>
              <div>
                <label className="overline block mb-2">Sort order</label>
                <input type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: e.target.value })} className="field" data-testid="pf-order" />
                <div className="text-[10px] text-black/50 mt-1">Lower numbers appear first (e.g. 10 shows before 20).</div>
              </div>
              <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_visible} onChange={e => setForm({ ...form, is_visible: e.target.checked })} data-testid="pf-visible" /> Visible</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_coming_soon} onChange={e => setForm({ ...form, is_coming_soon: e.target.checked })} data-testid="pf-coming" /> Coming Soon</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_featured} onChange={e => setForm({ ...form, is_featured: e.target.checked })} data-testid="pf-featured" /> Featured</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_imported} onChange={e => setForm({ ...form, is_imported: e.target.checked })} data-testid="pf-imported" /> Imported</label>
              </div>
            </div>

            <button type="button" onClick={() => setShowAdvanced(a => !a)} className="mt-5 overline text-black/50 underline" data-testid="pf-advanced-toggle">
              {showAdvanced ? "− Hide" : "+ Show"} pricing & ordering (private, for future use)
            </button>
            {showAdvanced && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-4 border-t border-black/10 pt-4">
                <div>
                  <label className="overline block mb-2">Wholesale $ (private)</label>
                  <input type="number" step="0.01" value={form.wholesale_price} onChange={e => setForm({ ...form, wholesale_price: e.target.value })} className="field" data-testid="pf-price" />
                </div>
                <div>
                  <label className="overline block mb-2">Min order qty</label>
                  <input type="number" value={form.min_order_qty} onChange={e => setForm({ ...form, min_order_qty: e.target.value })} className="field" data-testid="pf-moq" />
                </div>
                <div>
                  <label className="overline block mb-2">Low-stock alert @</label>
                  <input type="number" min="0" value={form.low_stock_threshold} onChange={e => setForm({ ...form, low_stock_threshold: e.target.value })} className="field" placeholder="Use global default" data-testid="pf-low-stock" />
                  <div className="text-[10px] text-black/50 mt-1">Overrides site default. Leave blank to use it.</div>
                </div>
                <label className="flex items-center gap-2 text-sm self-end pb-3"><input type="checkbox" checked={form.ordering_enabled} onChange={e => setForm({ ...form, ordering_enabled: e.target.checked })} data-testid="pf-ordering" /> Ordering enabled</label>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              <button onClick={save} className="btn-primary" data-testid="pf-save">{editId ? "Save changes" : "Add product"}</button>
              <button type="button" onClick={() => setPreview(form)} className="btn-ghost" data-testid="pf-preview">Preview</button>
              <button type="button" onClick={() => { setEditId(null); setShowForm(false); setForm(EMPTY_PRODUCT); }} className="btn-ghost" data-testid="pf-cancel">Cancel</button>
            </div>
          </div>
        )}

        {preview && (
          <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-6" onClick={() => setPreview(null)} data-testid="product-preview">
            <div className="bg-paper border border-ink hard-shadow max-w-md w-full" onClick={e => e.stopPropagation()}>
              <div className="aspect-square border-b border-ink overflow-hidden">
                {preview.image_url ? <img src={resolveImage(preview.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-secondary flex items-center justify-center overline">No image</div>}
              </div>
              <div className="p-5">
                <div className="overline text-black/50 mb-2">{catBySlug[preview.category_slug]?.name || "Category"}{preview.brand && ` · ${preview.brand}`}</div>
                <div className="font-bold">{preview.name || "Untitled"}</div>
                <div className="text-xs text-black/60 mt-2">{preview.case_size || preview.unit_size} {preview.sku && `· ${preview.sku}`}</div>
                {preview.description && <p className="text-xs text-black/60 mt-2">{preview.description}</p>}
                {preview.is_coming_soon && <div className="mt-3 inline-block bg-soda text-paper px-2 py-1 text-[10px] font-bold uppercase tracking-widest">Coming soon</div>}
              </div>
              <button onClick={() => setPreview(null)} className="btn-primary w-full">Close preview</button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6 items-center">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name / brand / SKU…" className="field max-w-xs text-sm py-2" data-testid="product-search" />
          <button onClick={() => setFilter("all")} className={`chip ${filter === "all" ? "bg-ink text-paper" : ""}`}>All ({list.length})</button>
          {cats.map(c => (
            <button key={c.id} onClick={() => setFilter(c.slug)} className={`chip ${filter === c.slug ? "bg-ink text-paper" : ""}`}>{c.name} ({list.filter(p => p.category_slug === c.slug).length})</button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="products-grid">
          {filtered.map(p => (
            <div key={p.id} className="border border-ink p-4 flex gap-3 bg-paper" data-testid={`admin-product-${p.id}`}>
              <div className="w-20 h-20 border border-ink overflow-hidden shrink-0 bg-secondary">
                {p.image_url ? <img src={resolveImage(p.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-black/40">—</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                  <span className={`chip text-[9px] ${p.is_visible ? "" : "text-black/40 border-black/30"}`}>{p.is_visible ? "Visible" : "Hidden"}</span>
                  {p.is_coming_soon && <span className="chip text-[9px] text-soda border-soda">Coming soon</span>}
                  {p.is_featured && <span className="chip text-[9px] text-cobalt border-cobalt">Featured</span>}
                  {p.is_imported && <span className="chip text-[9px]">Imported</span>}
                  {p.track_inventory && (
                    <span className={`chip text-[9px] ${p.stock_quantity === 0 ? "text-soda border-soda" : "text-cobalt border-cobalt"}`} data-testid={`stock-${p.id}`}>
                      Stock: {p.stock_quantity ?? 0}
                    </span>
                  )}
                </div>
                <div className="font-bold text-sm leading-tight truncate">{p.name}</div>
                <div className="text-xs text-black/60 mt-1 truncate">{catBySlug[p.category_slug]?.name || p.category_slug}{p.brand && ` · ${p.brand}`}</div>
                <div className="text-xs text-black/60">{p.case_size || p.unit_size}{p.sku && ` · ${p.sku}`}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={() => editProduct(p)} className="text-xs underline" data-testid={`edit-${p.id}`}>Edit</button>
                  <button onClick={() => duplicate(p)} className="text-xs underline" data-testid={`dup-${p.id}`}>Duplicate</button>
                  <button onClick={() => toggleVisible(p)} className="text-xs underline" data-testid={`hide-${p.id}`}>{p.is_visible ? "Hide" : "Publish"}</button>
                  <button onClick={() => remove(p)} className="text-xs underline text-soda" data-testid={`delete-${p.id}`}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
